import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 40px 32px;
  background: #fff;
  border-radius: 15px;
  font-family: 'serif';
`;

const Title = styled.h2`
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 32px;
  margin-top: 40px;
`;

const Section = styled.div`
  margin-bottom: 18px;
`;

const List = styled.ol`
  margin-left: 18px;
  margin-bottom: 18px;
  font-size: 1rem;
`;

const Statement = styled.p`
  margin: 18px 0 8px 0;
  font-size: 1rem;
`;

const Blank = styled.span`
  display: inline-block;
  min-width: 120px;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const SignatureRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 32px;
  font-size: 1rem;
`;

const SigLabel = styled.span`
  color: #222;
`;

const SigValue = styled.span`
  border-bottom: 1px solid #333;
  margin: 0 8px;
  padding: 0 4px;
  font-weight: 500;
`;

const SignatureImage = styled.img`
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border-bottom: 1px solid #333;
  margin-bottom: 16px;
  background: transparent;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;

  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const StudentAcknowledgmentForm = () => {
  const { email } = useParams();
  const { data: student, loading, error } = useEnrollmentFormData();
  const docRef = useRef();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return <Container><LoadingMessage>Loading student information...</LoadingMessage></Container>;
  }
  if (error || !student) {
    return <Container><ErrorMessage>{error || 'Student not found'}</ErrorMessage></Container>;
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString();

  return (
    <Container style={{fontFamily: 'sans-serif'}}>
      <PrintHideStyle />
      <div ref={docRef} style={{ background: '#fff', color: '#222', padding: 0 }}>
        <img src={process.env.PUBLIC_URL + '/forms_logo.png'} alt="CCLC Logo" style={{ display: 'block', margin: '0 auto 24px auto', maxWidth: 350 }} />
        <div style={{textAlign: 'center', fontSize: '0.98rem', marginBottom: 2}}>
          Chicago Campus: 840 W. Irving Park Road, suite 203, Illinois 60613 Office: (773) 506-1503<br/>
          Evergreen Campus: 9730 S. Western Ave, suite 502, Evergreen Park Illinois 60805
        </div>
        <div style={{textAlign: 'center', fontSize: '0.98rem', marginBottom: 12}}>
          <a href="https://www.cclctraining.org" target="_blank" rel="noopener noreferrer">www.cclctraining.org</a>
        </div>
        <Title>Student Acknowledgement</Title>
        <Section style={{ fontWeight: 500, marginBottom: 10 }}>The Applicant Understands:</Section>
        <List>
          <li style={{marginBottom: 20}}>Chicago Community Learning Center is an Illinois Board of Higher Education (IBHE) licensed facility as a Private Vocational School in Health-Career Education; we do not accept credit from previous education entities or any experimental learning, or CLEP.</li>
          <li style={{marginBottom: 20}}>Chicago Community Learning Center only assists with Job Placement in the Field of health-care study, but we DO NOT guarantee job placement.</li>
          <li style={{marginBottom: 20}}>Chicago Community Learning Center reserves the right to discontinue training for unsatisfactory progress, non-payment of fees or failure to abide by school guidelines.</li>
          <li style={{marginBottom: 20}}>Chicago Community Learning Center reserves the right to reschedule the training start date when the number of participants scheduled is too small.</li>
          <li style={{marginBottom: 20}}>Under WIOA tuition ITA, Chicago Community Learning Center only pays for license exams once for C.N.A State License, Patient Care Technician License with EKG & Phlebotomy. Retakes of any license exam is the responsibility of the student.</li>
        </List>
        <Statement>
          <Blank style={{ marginRight: 10 }}>{student.firstName} {student.lastName}</Blank> I have carefully read and received an exact copy of the acknowledgement
        </Statement>
        <Statement>
          <Blank style={{ marginRight: 10 }}>{student.firstName} {student.lastName}</Blank> I understand that Chicago Community Learning Center may terminate my Contract agreement if I fail to comply with attendance and financial requirement or if I disrupt the normal activities of training. I understand that my financial obligation to Chicago Community Learning Center must be paid in full before a certification may be awarded.
        </Statement>
        <SignatureRow style={{ marginTop: 40 }}>

          <SigLabel>Student Signature:</SigLabel>
          {(() => {
            // Get signature data from student
            const sigData = student.student_ack_signature_data;
            
            if (sigData) {
              // Check if it's a valid data URL (must have content after data:)
              if (typeof sigData === 'string' && sigData.startsWith('data:') && sigData.length > 6) {
                return (
                  <SignatureImage src={sigData} alt="Student Signature" />
                );
              }
              
              // Check if it's SVG markup
              if (typeof sigData === 'string' && sigData.trim().startsWith('<svg')) {
                return (
                  <div 
                    dangerouslySetInnerHTML={{ __html: sigData }}
                    style={{ maxHeight: 40, maxWidth: 200 }}
                  />
                );
              }
              
              // Check if it's a base64 string (without data URL prefix)
              if (typeof sigData === 'string' && sigData.length > 100 && /^[A-Za-z0-9+/=]+$/.test(sigData)) {
                return (
                  <SignatureImage 
                    src={`data:image/png;base64,${sigData}`} 
                    alt="Student Signature" 
                  />
                );
              }
              
              // Check if it's a file path
              if (typeof sigData === 'string' && (sigData.includes('.png') || sigData.includes('.jpg') || sigData.includes('.jpeg'))) {
                return (
                  <SignatureImage src={sigData} alt="Student Signature" />
                );
              }
            }
            
            // If no signature data available, show empty signature field with underline
            return <SigValue style={{marginRight:40}}></SigValue>;
          })()}

          <SigLabel>Printed:</SigLabel>
          <SigValue style={{marginRight:40}}>{student.firstName} {student.lastName}</SigValue>

        <SigLabel>Date:</SigLabel>
        <SigValue>{dateStr}</SigValue>
         
        </SignatureRow>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default StudentAcknowledgmentForm; 